import React, { RefObject } from 'react';
import FileSelector from "./FileSelector";
import { apiGet, apiPost, downloadAndOpenFile, getAgentUrl, getMessage, SpaceService } from "./Utilities";
import * as microsoftTeams from "@microsoft/teams-js";
import TeamDriveConnectionTest from "./TeamDriveConnectionTest";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconName } from '@fortawesome/fontawesome-common-types';
import { Button, Modal } from 'react-bootstrap';

import { TdFile, TdLoginInfo, TdSpace } from '../TdTypes';
import { compFn, mkCompFn, SortSelection } from './Sorting';

import {Trans, withTranslation, WithTranslation} from "react-i18next";
import { TeamsUserCredential } from '@microsoft/teamsfx';

export function isMobile(context : microsoftTeams.app.Context|null) {
  return ['desktop','web'].indexOf(context?.app.host.clientType ?? '') === -1;
}

export class FileItem extends React.Component<
{
  name: string,
  subtext?: string,
  icon?: IconName,
  onClick?: () => void,
  error?: string,
  info?: string
}
>{
  handleClick = () => {
    if (this.props.onClick)
      this.props.onClick();
  };

  render() {
    return <div className={"td-fileitem" + (this.props.onClick ? " clickable" : "")} onClick={this.handleClick}>
      { this.props.icon && <FontAwesomeIcon className="td-fileitem-icon" icon={this.props.icon} /> }
      <div className="td-fileitem-text">
        <span className="td-fileitem-title">{this.props.name}</span>
        { this.props.subtext && <span className="td-fileitem-subtext">{this.props.subtext}</span> }
      </div>
      { this.props.error && <div className="td-fileitem-error alert alert-danger">{this.props.error}</div> }
      { this.props.info && <div className="td-fileitem-error alert alert-info">{this.props.info}</div> }
      <div className="td-fileitem-btns">{ this.props.children }</div>
    </div>;
  }
}

type PartialFile = {
  globalId : string,
  error? : string,
  loaded? : boolean,
  file? : TdFile,
  space? : TdSpace
};

const SharedFileList = withTranslation()(class extends React.Component<WithTranslation & {
  openAction?: (file:PartialFile) => void,
  openLabel? : string,
  openAfterShare? : boolean
}> {
  state = {
    files: [] as PartialFile[],
    loaded: false,
    error: '',
    status: '',
    teamsContext: null as microsoftTeams.app.Context|null,
    enableGuiActions: false,
    tdConnectionOk: false,
    isMobile: false
  };

  private listSortOptions : [string, compFn<PartialFile>][] = [
    [this.props.t('Sort by name'), mkCompFn(i => i.file?.name as string, (n1, n2) => n1.localeCompare(n2))], 
    [this.props.t('Sort by Space'), mkCompFn(i => i.space?.name as string, (n1, n2) => n1.localeCompare(n2))]
  ];

  callApi = (method : string, params : Record<string,any>) : Promise<any> => {

    const credential = new TeamsUserCredential({
      clientId: process.env.REACT_APP_CLIENT_ID!,
      initiateLoginEndpoint: process.env.REACT_APP_START_LOGIN_PAGE_URL!
    });
    
    return credential.getToken("").then(accessToken => {
      let url = `${process.env.REACT_APP_FUNC_ENDPOINT!}/api/getSharedFiles`;
      const conf : any = {
        method: method,
        headers: {
            "Authorization": `Bearer ${accessToken!.token}`
        }
      };

      if (method === "GET") {
        url += "?" + new URLSearchParams(params);
      } else {
        conf.headers["content-type"] = "application/json";
        conf.body = JSON.stringify(params);
      }

      return fetch(url, conf);
    })
    .then(res => res.json())
    .then(response => {
      if (response.error) {
        return Promise.reject({message: response.error});
      } else {
        return response.data;
      }
    });
  };

  loadSharedFiles = () => {
    this.setState({ loaded: false });

    this.callApi("GET", {
      "teamId": this.state.teamsContext!.team!.internalId,
      "channelId": this.state.teamsContext!.channel!.id
    }).then((rows : any[]) => {
        this.setState({
          files: rows.map(r => { return { globalId: r.GlobalFileID }}), 
          loaded: true, 
          error: ''
        }, this.loadFileData);
        
    }).catch(error => {
      this.setState({ error: this.props.t('Unable to contact DB server: ') + getMessage(error) });
    });
  };

  loadFileData = () => {
    this.state.files.forEach(r => {
      if (this.state.isMobile) {
        // can't load any data on mobile since there is no client to connect to
        r.loaded = true;
        r.error = '';
        this.sortList();
      } else {
        r.loaded = false;
        r.error = '';

        apiGet('getFile', {
          "id": r.globalId
        }).then(data => {
          r.file = data;
          if (r.file?.trashed)
            r.error = this.props.t('File has been deleted from the Space');
          return SpaceService.getSpace(data.spaceId);
        }).then(space => {
          r.space = space;
        }).catch(err => {
          r.error = err.message;
        }).finally(() => {
          r.loaded = true;
          this.sortList();
        });
      }
    });
  };

  sortList = (sortOption = 0, reverse = false) => {
    this.setState({
      files: this.state.files.sort((a, b) => this.listSortOptions[sortOption][1](a, b, reverse))
    });
  }

  addSharedFile = (file : TdFile) => {
    this.setState({ error: '' });

    this.callApi("POST", {
      "teamId": this.state.teamsContext!.team!.internalId,
      "channelId": this.state.teamsContext!.channel!.id,
      "fileId": file.globalId,
      "ownerId": this.state.teamsContext!.user!.id
    })
    .then(() => {
      this.loadSharedFiles();

      if (this.props.openAfterShare && this.props.openAction)
        this.props.openAction({
          globalId: file.globalId,
          file: file
        });
    })
    .catch(error => {
      this.setState({ error: error.message });
    });
  };

  removeSharedFile = (globalFileId : string) => {
    this.setState({ error: '' });

    this.callApi("DELETE", {
      "teamId": this.state.teamsContext!.team?.internalId,
      "channelId": this.state.teamsContext!.channel?.id,
      "fileId": globalFileId
    })
    .then(() => {
      this.loadSharedFiles();
    })
    .catch(error => {
      this.setState({ error: error.message });
    });
  };

  componentDidMount() {
    // load initial data
    this.setState({status: this.props.t('Initializing teams context...'), error: ''});
    
    microsoftTeams.app.getContext().then(context => {
      this.setState({
        status: '',
        isMobile: isMobile(context)
      }, () => {
        if (!context.team?.internalId || !context.channel?.id) {
          this.setState({error: this.props.t('Unable to load Team-Metadata')});
          console.error(`Required IDs not available: teamId: '${context.team?.internalId}', channelId: '${context.channel?.id}'`);
        } else {
          this.setState({teamsContext: context}, () => {
            this.loadSharedFiles();
          });
        }
      });
    });
    apiGet<TdLoginInfo>('getLoginInformation').then(info => {
      this.setState({ 
        enableGuiActions: !info.isDaemon,
        tdConnectionOk: true
       });
    }).catch(() => {
      // teams complains about an unhandled rejection if we don't catch the error here, and then appears to freeze -
      // handling connection errors is handled by the TeamDriveConnectionTest component, and so doesn't need to be
      // done here
    });
  };

  private showFileInfo(file : TdFile) {
    if (this.state.enableGuiActions) {
      SpaceService.getSpace(file.spaceId).then(space => {
        return apiPost("contextMenuAction", {
          id: 3,
          path: space.spaceRoot + file.path + file.name
        });
      }).catch(err => this.setState({error: err.message}));
    } else {
      window.open(getAgentUrl() + '/fileref/' + file.globalId);
    }
  }

  private fileItemTitle(itm : PartialFile, fileIndex : number) : string {
    if (this.state.isMobile) 
      return `${this.props.t('File')} #${fileIndex + 1}`;
    return itm.file?.name ?? this.props.t('<unknown file>');
  }

  private fileItemSubtext(itm : PartialFile) : string {
    if (this.state.isMobile)
      return this.props.t('Unable to show file names on mobile devices');
    return (itm.space && itm.file) ? (itm.space.name + itm.file.path) : this.props.t('<unknown Space>');
  }

  render() {
    const t = this.props.t;

    return (
      <div>
        { this.state.error && (<div className="alert alert-danger">{this.state.error}</div>)}
        { this.state.status && (<div className="alert alert-info">{this.state.status}</div>)}
        { this.state.loaded && this.state.files.length === 0 && (
          <ul><li>{t('No files shared yet')}</li></ul>
        )}
        { !this.state.loaded && (
          <ul><li>{t('Loading...')}</li></ul>
        )}
        { this.state.loaded && this.state.files.length > 0 && (
          <div className="shared-files-list">
            { !this.state.isMobile && <div className="sort-row">
              <SortSelection onChange={this.sortList} options={this.listSortOptions.map(o => o[0])}></SortSelection>
            </div> }
            { this.state.files.map((itm, idx) => <span key={idx}>
              { itm.loaded && (
                <FileItem 
                  name={this.fileItemTitle(itm, idx)} 
                  subtext={this.fileItemSubtext(itm)}  
                  icon="file"
                  error={(itm.file && itm.error) ? itm.error : ''}
                  info={(!itm.file && !this.state.isMobile) ? t('Unable to access Space, please request invitation.') : ''}
                  >
                  { !itm.error && this.props.openAction && (itm.file || this.state.isMobile) &&
                    <div className="btn-section">
                      <button className="btn btn-primary btn-sm" onClick={() => this.props.openAction!(itm)}>{this.props.openLabel ?? t('open')}</button>
                    </div>}
                  <div className="btn-section">
                    { !itm.error && itm.file && !this.state.isMobile && 
                      <button className="btn single-char-btn" onClick={() => this.showFileInfo(itm.file!)}><FontAwesomeIcon icon='info' /></button>
                    }
                    <button className="btn single-char-btn" onClick={() => this.removeSharedFile(itm.globalId)}><FontAwesomeIcon icon='trash-can' /></button>
                  </div>
                  { this.props.children }
                </FileItem>
              )}
              { !itm.loaded && (
                  <span>{t('Loading...')}<br/></span>
              )}
            </span>)}
          </div>
        )}

        { this.state.tdConnectionOk && !this.state.isMobile && <FileSelector callback={this.addSharedFile} /> }
      </div>
    );
  }
});

export const LoadingIndicator = withTranslation()(class extends React.Component<WithTranslation> {
  render() {
    return (
      <div className="alert alert-info text-center">
        <FontAwesomeIcon icon="rotate-right" spin className='icon'/>&nbsp;
        { this.props.children ?? <b>{this.props.t('Loading...')}</b> }
      </div>
    );
  }
});

class SharedFiles extends React.Component<WithTranslation> {
  state = {
    error: '',
    downloadingFile: null as TdFile|null,
    isMobile: false,
    context: null as null|microsoftTeams.app.Context
  };

  openFile = (file : PartialFile) => {
    if (this.state.isMobile) {
      // open the file in the webportal
      window.open(`https://webportal.teamdrive.net/fileref/${file.globalId}`, "_blank", "noreferrer");
    } else {
      this.setState({error: '', downloadingFile: file.file});

      downloadAndOpenFile(file).catch(reason => {
        this.setState({error: reason.message});
      }).finally(() => {
        this.setState({downloadingFile: null});
      });
    }
  };

  componentDidMount() {
    // load initial data
    microsoftTeams.app.getContext().then(context => {
      this.setState({
        context: context,
        isMobile: isMobile(context)
      });
    });
  };

  render() {
    const t = this.props.t;
    
    return (
      <div className="tab-container">
        {/* { this.state.isMobile && <MobileNotSupportedInfo></MobileNotSupportedInfo>} */}
        { this.state.context && (<>
          { !this.state.isMobile && <TeamDriveConnectionTest/> }
          <h3>{t('Files shared with the team:')}</h3>
          { this.state.error && (<div className="alert alert-danger">{this.state.error}</div>)}
          <SharedFileList openAction={this.openFile} openLabel={this.state.isMobile ? t('Open in Webportal') : t('open')} />

          { this.state.downloadingFile !== null && 
            <Modal show={true} className="download-progress-modal">
              <Modal.Body className="text-center">
                <LoadingIndicator>
                  <Trans i18nKey="openingFileProgress">Opening <b>{{filename: this.state.downloadingFile.name}}</b>...</Trans>
                </LoadingIndicator>
              </Modal.Body>
            </Modal>
          }
        </>)}
      </div>
    );
  }
}
export default withTranslation()(SharedFiles);

/**
 * Component that is shown when selecting a file to link to in a message
 */
export const SharedFileSelector = withTranslation()(class extends React.Component<WithTranslation>{
  state = {
    error: '',
    context: null as null|microsoftTeams.app.Context,
    isInTeam: true
  };
  private inputRef : RefObject<HTMLInputElement>;

  constructor(props : any) {
    super(props);
    this.inputRef = React.createRef();
  }

  selectFile = (f : PartialFile) => {
    if (f.file) {
      // link to tab:
      SpaceService.getSpace(f.file.spaceId).then(space => {
        microsoftTeams.dialog.url.submit({
          name: f.file!.name,
          globalId: f.file!.globalId,
          space: space.name,
          path: f.file!.path,
          creator: f.file!.creator,
          size: f.file!.size
        });
      });
    } else {
      microsoftTeams.dialog.url.submit({
        globalId: f.globalId
      });
    }
  };

  shareFileRefUrl = (url : string) => {
    // file reference format:
    // https://regtest35.teamdrive.net/yvva/ref/teamdrive:3CN04K00004ZS005VZ9KSK4YF2RNGRE0?space=Test%20Space&file=Invoice-2023-023721.pdf&size=243-KB
    // https://<regserver>/yvva/ref/teamdrive:<globalId>?space=<space name>&file=<file name>&size=<human-readable size>

    const url_obj = new URL(url);
    
    microsoftTeams.dialog.url.submit({
      name: url_obj.searchParams.get("file"),
      globalId: url_obj.pathname.split(':').pop(),
      space: url_obj.searchParams.get("space"),
      sizeStr: url_obj.searchParams.get("size")?.replace('-',' ')
    });
  }

  componentDidMount() {
    microsoftTeams.app.getContext().then(context => {
      this.setState({
        context: context,
        isInTeam: context.team?.internalId && context.channel?.id
      });
    });
  };

  render() {
    return (
      <div className="dialog-container">
        { !this.state.context && <LoadingIndicator></LoadingIndicator>}
        
        {/* { isMobile(this.state.context) && <MobileNotSupportedInfo></MobileNotSupportedInfo>} */}
        { this.state.context && this.state.isInTeam && !isMobile(this.state.context) && <>
          <h3>{this.props.t('Select file link to')}:</h3>
          { this.state.error && (<div className="alert alert-danger">{this.state.error}</div>)}
          <SharedFileList openAction={this.selectFile} openLabel={this.props.t('Create Link')} openAfterShare={true} />
        </> }
        { (isMobile(this.state.context) || !this.state.isInTeam) && <>
          <Trans i18nKey='shareFileInstructions'>
            <div className='alert alert-info'>
              To share a file from TeamDrive:
              <ul>
                <li>open the TeamDrive App</li>
                <li>navigate to a file</li>
                <li>click the &#39;export&#39; option (an arrow poiting to the right)</li>
                <li>select &#39;Copy File Reference&#39;</li>
                <li>paste the link into the textfield below</li>
              </ul>
            </div>
          </Trans>
          <input type='text' className='file-ref-input' ref={this.inputRef} placeholder={this.props.t('File reference URL from TeamDrive')} /><br/>
          <Button variant='primary' onClick={() => this.shareFileRefUrl(this.inputRef.current!.value)}>{this.props.t('Share File')}</Button> 
        </>}
      </div>
    );
  }
});
